import { useContext } from 'react'

import {
  Button, Checkbox, ListItem, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject, snakeToTitleCase } from '@domain-group/javascript-utils'

import useService, { useServiceForm } from '@hooks/useService'

import { serviceOptions } from '@functions/service'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'EditServiceModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateService: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditServiceModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateService } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { service } = modalPayload

  const {
    callbacks: { updateService: updateFn },
    updating,
  } = useService(service)

  const {
    entityState,
    entityState: {
      options,
      short_name,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useServiceForm(service, { validateOn: 'change' })

  const updateServicePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Service Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Service" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Short Name" errorMessage={errors.short_name} marginTop="large">
            <input name="short_name" type="text" value={short_name} {...handlers} />
          </Form.Field>
        </Form>

        <Form.Field label="Service Options" marginTop="large">
          {serviceOptions.map((option) => {
            const isChecked = digObject(options, option, false)

            return (
              // @ts-expect-error TS2339
              <ListItem.WithCheckbox
                boxProps={{ border: 'none' }}
                checkbox={(
                  <Checkbox
                    checked={isChecked}
                    onClick={() => setEntityState({ options: { ...options, [option]: !isChecked } })}
                  />
                )}
                disableHover
                key={option}
              >
                <Text color="bodyFontColor" fontSize="small">
                  {snakeToTitleCase(option)}
                </Text>
                {/*
               // @ts-expect-error TS2339 */}
              </ListItem.WithCheckbox>
            )
          })}
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateService(updateServicePayload)}
          size="large"
        >
          {saveEnabled ? 'Update Service' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditServiceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
